import { EntityFactory, EntityRef, IPageDraft, Page, PageDraft } from "@amzn/ask-legal-domain";
import {
    Box,
    Button,
    ButtonDropdown,
    ButtonDropdownProps,
    Grid,
    Icon,
    SpaceBetween,
    Spinner,
    StatusIndicator
} from "@amzn/awsui-components-react";
import { Builder } from "builder-pattern";
import * as React from "react";
import { useEffect } from "react";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../../hooks/api-hook";
import { UIModel } from "../../model/ui-model";
import { AppContext } from "../../setup/context";
import { DateTimeFormatter } from "../../utils/date-time-utils";
import { ErrorFlashbar } from "../common/ErrorFlashbar";
import { DraftConflictModal } from "../common/DraftConflictModal";
import { IdentityLink } from "../common/IdentityLink";
import { PageDraftVersionHistory } from "../page-draft/PageDraftVersionHistoryTable";

export namespace PageVersionHistory {
    export const TableView = (props: {
        state: UIModel.State<Page.Data>
    }) => {
        const context = useContext(AppContext);
        const expandedVersionState = UIModel.State.use<number>({
            initialValue: -1
        });

        const loadAllVersionsRunner = useAPI(
            context.getPageAPI().loadAllVersions
        );

        useEffect(() => {
            loadAllVersionsRunner.submitRun(
                EntityFactory.toEntityRef(props.state.value)
            );
        }, [
            props.state.value.id,
            props.state.value.version
        ]);

        return (
            <Box margin={{left: "xxl", right: "xxl"}}>
                <SpaceBetween direction="vertical" size="s">
                    <Grid gridDefinition={[
                        { colspan: 2 },
                        { colspan: 2 },
                        { colspan: 2 },
                        { colspan: 2 },
                        { colspan: 2 },
                        { colspan: 2 }]}>
                        <div>Version number</div>
                        <div>Contributors</div>
                        <div>Reviewer</div>
                        <div>Published by</div>
                        <div>Published Date</div>
                        <div>Actions</div>
                    </Grid>
                    {loadAllVersionsRunner.status === "Error" && (
                        <ErrorFlashbar error={loadAllVersionsRunner.data.err}/>
                    )}
                    {loadAllVersionsRunner.status === "Running" && (
                        <Spinner></Spinner>
                    )}
                    {loadAllVersionsRunner.status === "Succeeded" && (
                        loadAllVersionsRunner.data.output.sort((p1, p2) => p2.version - p1.version).map(page =>
                            <PageVersionHistory.TableRow
                                data={page}
                                currentVersion={props.state.value.version}
                                expanded={expandedVersionState}/>
                        )
                    )}
                </SpaceBetween>
            </Box>
        );
    };

    export const TableRow = (props: {
        data: Page.Data;
        currentVersion: number;
        expanded: UIModel.State<number>;
    }) => {
        const navigate = useNavigate();
        const context = useContext(AppContext);
        const draftHistoryRunner = useAPI(
            context.getPageDraftAPI().loadVersion
        );
        const createFromVersionRunner = useAPI(
            context.getPageDraftAPI().createFromVersion
        );
        const discardRunner = useAPI(
            context.getPageDraftAPI().discard
        );
        const [showConflictModal, setShowConflictModal] = React.useState(false);
        const createInitiated = React.useRef(false);
        const discardInitiated = React.useRef(false);
        const postDiscardCreate = React.useRef(false);

        useEffect(() => {
            draftHistoryRunner.submitRun(
                props.data.publishedFromDraft
            );
        }, [
            props.data.id,
            props.data.version
        ]);

        // Navigate to draft editor on successful creation
        useEffect(() => {
            if (!createInitiated.current) return;
            if (createFromVersionRunner.status === "Running") {
                // Clear the post-discard flag once the runner leaves the stale error state.
                // This ensures genuine errors from the new request are always surfaced.
                postDiscardCreate.current = false;
                return;
            }
            if (createFromVersionRunner.status === "Succeeded") {
                createInitiated.current = false;
                // Force full page reload — navigate() doesn't re-render when already on admin page
                window.location.assign(`/#/page/${props.data.id}/admin?tab=draftPage`);
            } else if (createFromVersionRunner.status === "Error") {
                if (postDiscardCreate.current) {
                    // Stale 409 from before the discard — ignore it
                    return;
                }
                if (createFromVersionRunner.data.err?.code === 409) {
                    setShowConflictModal(true);
                }
                createInitiated.current = false;
            }
        }, [createFromVersionRunner.status, createFromVersionRunner.data, props.data.id]);

        // After successful discard, create new draft from the selected version
        useEffect(() => {
            if (!discardInitiated.current) return;
            if (discardRunner.status === "Succeeded") {
                discardInitiated.current = false;
                setShowConflictModal(false);
                createInitiated.current = true;
                postDiscardCreate.current = true;
                createFromVersionRunner.submitRun(
                    IPageDraft.CreateFromVersionInput.create({
                        pageId: props.data.id,
                        sourceVersion: props.data.version
                    })
                );
            } else if (discardRunner.status === "Error") {
                // Don't reset discardInitiated here — keep it true so the error displays in the modal.
                // It gets reset when the user closes the modal (onCancel).
            }
        }, [discardRunner.status, discardRunner.data, props.data.id, props.data.version]);

        const isCurrentVersion = props.data.version === props.currentVersion;

        const dropdownItems: ButtonDropdownProps.Items = isCurrentVersion
            ? [ButtonDropdownItems.View]
            : [ButtonDropdownItems.View, ButtonDropdownItems.CreateDraft];

        const buttonDropdownEventHandler = (event: { detail: { id: string } }) => {
            switch (event.detail.id) {
                case ButtonDropdownItems.View.id:
                    navigate(`/page/${props.data.id}/live?version=${props.data.version}`);
                    break;
                case ButtonDropdownItems.CreateDraft.id:
                    createInitiated.current = true;
                    createFromVersionRunner.submitRun(
                        IPageDraft.CreateFromVersionInput.create({
                            pageId: props.data.id,
                            sourceVersion: props.data.version
                        })
                    );
                    break;
                default:
                    break;
            }
        };

        return (
            <React.Fragment>
                <SpaceBetween direction="vertical" size="s">
                    <Grid gridDefinition={[
                        { colspan: 2 },
                        { colspan: 2 },
                        { colspan: 2 },
                        { colspan: 2 },
                        { colspan: 2 },
                        { colspan: 2 }]}>
                        <div>
                            {props.expanded.value === props.data.version ? (
                                <Button
                                    variant="icon"
                                    iconName="caret-down-filled"
                                    onClick={() => props.expanded.setValue(-1)}
                                />
                            ) : (
                                <Button
                                    variant="icon"
                                    iconName="caret-right-filled"
                                    onClick={() => props.expanded.setValue(props.data.version)}
                                />
                            )}
                            {props.data.version}
                        </div>
                        <div>
                            {draftHistoryRunner.status === "Running" && <StatusIndicator type="loading">
                                Loading
                            </StatusIndicator>}
                            {draftHistoryRunner.status === "Error" && <StatusIndicator type="error">
                                Error
                            </StatusIndicator>}
                            {draftHistoryRunner.status === "Succeeded" && draftHistoryRunner.data.output.contributors
                                .map(c =>
                                    <IdentityLink id={c.id} name={c.name}/>
                                )}
                        </div>
                        <div>
                            {draftHistoryRunner.status === "Running" && <StatusIndicator type="loading">
                                Loading
                            </StatusIndicator>}
                            {draftHistoryRunner.status === "Error" && <StatusIndicator type="error">
                                Error
                            </StatusIndicator>}
                            {draftHistoryRunner.status === "Succeeded" && draftHistoryRunner.data.output.reviewedBy ? <IdentityLink
                                id={draftHistoryRunner.data.output.reviewedBy.id}
                                name={draftHistoryRunner.data.output.reviewedBy.name}
                            /> : <></>}
                        </div>
                        <div>
                            {draftHistoryRunner.status === "Running" && <StatusIndicator type="loading">
                                Loading
                            </StatusIndicator>}
                            {draftHistoryRunner.status === "Error" && <StatusIndicator type="error">
                                Error
                            </StatusIndicator>}
                            {draftHistoryRunner.status === "Succeeded" && draftHistoryRunner.data.output.publishedBy ? (
                                <IdentityLink
                                    id={draftHistoryRunner.data.output.publishedBy.id}
                                    name={draftHistoryRunner.data.output.publishedBy.name}
                                />) : <></>
                            }
                        </div>
                        <div>
                            {draftHistoryRunner.status === "Running" && <StatusIndicator type="loading">
                                Loading
                            </StatusIndicator>}
                            {draftHistoryRunner.status === "Error" && <StatusIndicator type="error">
                                Error
                            </StatusIndicator>}
                            {draftHistoryRunner.status === "Succeeded" &&
                                DateTimeFormatter.formatDate(draftHistoryRunner.data.output.lastModifiedDate)
                            }
                        </div>
                        <div>
                            <ButtonDropdown
                                loading={createFromVersionRunner.status === "Running"}
                                items={dropdownItems}
                                onItemClick={buttonDropdownEventHandler}>
                                <Icon name="menu" />
                            </ButtonDropdown>
                        </div>
                    </Grid>
                    {/* Show non-conflict errors inline */}
                    {createFromVersionRunner.status === "Error" && createFromVersionRunner.data.err?.code !== 409 && (
                        <ErrorFlashbar error={createFromVersionRunner.data.err}/>
                    )}
                    {props.expanded.value === props.data.version && (
                        <PageDraftVersionHistory entityRef={props.data.publishedFromDraft}/>
                    )}
                </SpaceBetween>
                {/* Conflict modal: draft already exists for this page */}
                {showConflictModal && (
                    <DraftConflictModal
                        onContinueEditing={() => {
                            setShowConflictModal(false);
                            navigate(`/page/${props.data.id}/admin?tab=draftPage`);
                        }}
                        onDiscardAndCreate={() => {
                            // Discard existing draft; useEffect will trigger createFromVersion on success
                            const draftId = PageDraft.Factory.toPageDraftId(
                                Builder<EntityRef>().id(props.data.id).version(props.currentVersion).build()
                            );
                            discardInitiated.current = true;
                            discardRunner.submitRun(draftId);
                        }}
                        isDiscarding={discardRunner.status === "Running"}
                        discardError={discardInitiated.current && discardRunner.status === "Error" ? discardRunner.data.err : undefined}
                        onCancel={() => {
                            discardInitiated.current = false;
                            setShowConflictModal(false);
                        }}
                    />
                )}
            </React.Fragment>
        );
    };

    export namespace ButtonDropdownItems {
        export const View = Builder<ButtonDropdownProps.Item>()
            .id("view")
            .text("View")
            .build();
        export const CreateDraft = Builder<ButtonDropdownProps.Item>()
            .id("create-draft")
            .text("Create Draft from this Version")
            .build();
    }
}
